/**
 * Stands in for `next/router` inside the Knockout host — aliased over it by
 * backend/static/webpack.islands.js, so the Next build never sees this file.
 *
 * Navigation and path params are injected rather than imported: page.js belongs to
 * backend/static, and this file is also compiled and type-checked by the frontend, which has no
 * such dependency.
 *
 * Prefer passing ids to an island as props; this exists for components that read
 * the router internally.
 */

export interface IslandNavigation {
  push(url: string): void;
  replace(url: string): void;
  back(): void;
  getRouteParams?(): Record<string, string>;
}

let navigation: IslandNavigation | null = null;

export function configureIslandRouter(nav: IslandNavigation): void {
  navigation = nav;
}

export interface NextRouter {
  pathname: string;
  asPath: string;
  query: Record<string, string | string[] | undefined>;
  push(url: string): Promise<boolean>;
  replace(url: string): Promise<boolean>;
  back(): void;
}

const TENANT_PATH = /^\/t\/([^/]+)/;

/** Search params, then the host's route params, then `tenant_slug` off the `/t/<slug>/` prefix. */
function currentQuery(): Record<string, string | string[] | undefined> {
  const query: Record<string, string | string[] | undefined> = {};

  new URLSearchParams(window.location.search).forEach((value, key) => {
    const existing = query[key];
    if (existing === undefined) {
      query[key] = value;
    } else if (Array.isArray(existing)) {
      existing.push(value);
    } else {
      query[key] = [existing, value];
    }
  });

  // After the search params, so a spoofed ?trial-id=… cannot shadow the real route.
  if (navigation?.getRouteParams) {
    Object.assign(query, navigation.getRouteParams());
  }

  const tenant = TENANT_PATH.exec(window.location.pathname);
  if (tenant) {
    query.tenant_slug = tenant[1];
  }

  return query;
}

export const router: NextRouter = {
  get pathname() {
    return window.location.pathname;
  },
  get asPath() {
    return window.location.pathname + window.location.search;
  },
  get query() {
    return currentQuery();
  },
  push(url: string) {
    navigation?.push(url);
    return Promise.resolve(true);
  },
  replace(url: string) {
    navigation?.replace(url);
    return Promise.resolve(true);
  },
  back() {
    navigation?.back();
  },
};

export function useRouter(): NextRouter {
  return router;
}

export default router;
